import React, { useEffect, useState } from "react";
import {
  Box, Text, VStack, HStack, Badge, Flex, Spinner,
  useColorModeValue, Heading, Table, Thead, Tbody,
  Tr, Th, Td, Button, IconButton, Collapse, Input,
  FormControl, FormLabel, Modal, ModalOverlay,
  ModalContent, ModalHeader, ModalCloseButton,
  ModalBody, ModalFooter, useDisclosure, useToast,
  Select, Textarea, Checkbox, Divider, Icon,Tooltip,
} from "@chakra-ui/react";
import {
  FaChevronDown, FaChevronRight, FaKey,
  FaUser, FaShoppingBag, FaUndo,
} from "react-icons/fa";
import axios from "axios";
import { fmtLocal } from "../../utils/dates";
import { useNavigate } from "react-router-dom";



interface AdminEntitlementSummary {
  entitlement_id: number;
  purchase_id: number;
  content_id: number;
  current_content_type: string;
  status: string;
  start_date?: string;
  end_date?: string;
  practice_set_title?: string;
}

interface AdminPurchaseSummary {
  purchase_id: number;
  purchase_reference: string;
  status: string;
  total_amount: number;
  currency: string;
  completed_at?: string;
  is_test_purchase: boolean;
  entitlements: AdminEntitlementSummary[];
}

interface AdminAccountSummary {
  account_id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  has_active_entitlement: boolean;
  purchases: AdminPurchaseSummary[];
  contact_no?: string;
  street_address?: string;
  suburb?: string;
  postcode?: string;
  state?: string;
  country?: string;
}

const formatAddress = (a: AdminAccountSummary) => {
  const locality = [a.suburb, a.state, a.postcode].filter(Boolean).join(" ");
  return [a.street_address, locality, a.country].filter(Boolean).join(", ") || "—";
};

const B2CAccountManagement = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState<AdminAccountSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [expandedAccountIds, setExpandedAccountIds] = useState<Set<number>>(new Set());
  const [expandedPurchaseIds, setExpandedPurchaseIds] = useState<Set<number>>(new Set());

  // Password reset modal
  const [selectedAccount, setSelectedAccount] = useState<AdminAccountSummary | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const { isOpen: isPwOpen, onOpen: onPwOpen, onClose: onPwClose } = useDisclosure();

  // Refund modal
  const [selectedPurchase, setSelectedPurchase] = useState<AdminPurchaseSummary | null>(null);
  const [refundReason, setRefundReason] = useState("");
  const [refundNotes, setRefundNotes] = useState("");
  const [expireEntitlements, setExpireEntitlements] = useState(true);
  const [refundLoading, setRefundLoading] = useState(false);
  const { isOpen: isRefundOpen, onOpen: onRefundOpen, onClose: onRefundClose } = useDisclosure();

  const toast = useToast();
  const apiUrl = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("b2c_token");

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const mutedText = useColorModeValue("gray.500", "gray.400");
  const theadBg = useColorModeValue("gray.50", "gray.700");
  const rowHoverBg = useColorModeValue("gray.50", "gray.750");
  const expandedBg = useColorModeValue("gray.50", "gray.750");
  const purchaseBg = useColorModeValue("white", "gray.700");
  const purchaseHeaderBg = useColorModeValue("blue.50", "blue.900");

  const fetchAccounts = async (filter: string) => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/b2c/admin/accounts`, {
        params: filter ? { status_filter: filter } : {},
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccounts(response.data);
    } catch {
      toast({ title: "Failed to load accounts", status: "error", duration: 3000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAccounts(statusFilter); }, [statusFilter]);

  const toggleAccount = (id: number) => {
    setExpandedAccountIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const togglePurchase = (id: number) => {
    setExpandedPurchaseIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const openPasswordReset = (account: AdminAccountSummary) => {
    setSelectedAccount(account);
    setNewPassword("");
    onPwOpen();
  };

  const openRefund = (purchase: AdminPurchaseSummary) => {
    setSelectedPurchase(purchase);
    setRefundReason("");
    setRefundNotes("");
    setExpireEntitlements(true);
    onRefundOpen();
  };

  const handlePasswordReset = async () => {
    if (!selectedAccount) return;
    if (newPassword.length < 8) {
      toast({ title: "Password must be at least 8 characters", status: "error", duration: 3000 });
      return;
    }
    setPwLoading(true);
    try {
      await axios.put(
        `${apiUrl}/b2c/admin/account/reset-password`,
        { account_id: selectedAccount.account_id, new_password: newPassword, set_as_initial: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: `Password reset for ${selectedAccount.email}`,
        description: "Student will be prompted to change it on next login.",
        status: "success", duration: 4000,
      });
      onPwClose();
    } catch (error: any) {
      toast({
        title: error.response?.data?.detail ?? "Failed to reset password",
        status: "error", duration: 3000,
      });
    } finally {
      setPwLoading(false);
    }
  };

  const handleRefund = async () => {
    if (!selectedPurchase) return;
    setRefundLoading(true);
    try {
      await axios.post(
        `${apiUrl}/b2c/stripe/refund`,
        {
          purchase_id: selectedPurchase.purchase_id,
          refund_reason: refundReason || null,
          refund_notes: refundNotes || null,
          expire_entitlements: expireEntitlements,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({
        title: "Refund processed successfully",
        description: `Purchase ${selectedPurchase.purchase_reference} has been refunded.`,
        status: "success", duration: 4000,
      });
      onRefundClose();
      fetchAccounts(statusFilter);
    } catch (error: any) {
      toast({
        title: error.response?.data?.detail ?? "Refund failed",
        status: "error", duration: 4000,
      });
    } finally {
      setRefundLoading(false);
    }
  };

  const statusColorScheme = (status: string) => {
    switch (status) {
      case "COMPLETED": return "green";
      case "REFUNDED": return "orange";
      case "FAILED": return "red";
      case "PENDING": return "yellow";
      default: return "gray";
    }
  };

  return (
    <Box maxW="1400px" mx="auto" p={6}>
      <VStack align="start" spacing={1} mb={6}>
        <Heading size="lg">Account Management</Heading>
        <Text color={mutedText}>B2C student accounts, purchases and entitlements</Text>
      </VStack>

      {/* Filter */}
      <HStack mb={5} spacing={4}>
        <FormControl maxW="200px">
          <FormLabel fontSize="sm">Filter by status</FormLabel>
          <Select size="sm" value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}>
            <option value="">All Accounts</option>
            <option value="active">Active Only</option>
            <option value="expired">Expired Only</option>
          </Select>
        </FormControl>
        <Box pt={6}>
          <Text fontSize="sm" color={mutedText}>
            {accounts.length} account{accounts.length !== 1 ? "s" : ""} found
          </Text>
        </Box>
      </HStack>

      {loading ? (
        <Flex justify="center" py={16}><Spinner size="xl" /></Flex>
      ) : (
        <Box bg={cardBg} borderRadius="xl" borderWidth="1px"
          borderColor={borderColor} overflow="hidden">
          <Table variant="simple" size="sm">
            <Thead bg={theadBg}>
              <Tr>
                <Th w="40px" />
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Contact</Th>
                <Th>Address</Th>
                <Th>Status</Th>
                <Th>Purchases</Th>
                <Th>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {accounts.map(account => {
                const isExpanded = expandedAccountIds.has(account.account_id);
                const completedPurchases = account.purchases.filter(
                  p => p.status === "COMPLETED"
                ).length;

                return (
                  <React.Fragment key={account.account_id}>
                    {/* Account row */}
                    <Tr _hover={{ bg: rowHoverBg }} cursor="pointer"
                      onClick={() => toggleAccount(account.account_id)}>
                      <Td>
                        <IconButton aria-label="Expand" size="xs" variant="ghost"
                          icon={isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                          onClick={e => { e.stopPropagation(); toggleAccount(account.account_id); }}
                        />
                      </Td>
                      <Td>
                        <HStack>
                          <Icon as={FaUser} color={mutedText} />
                          <Text fontWeight="medium" fontSize="sm">
                            {[account.first_name, account.last_name].filter(Boolean).join(" ") || "—"}
                          </Text>
                        </HStack>
                      </Td>
                      <Td fontSize="sm">{account.email}</Td>
                      <Td fontSize="sm" color={mutedText}>{account.contact_no || "—"}</Td>
                      <Td fontSize="xs" color={mutedText} maxW="260px">
                        <Tooltip label={formatAddress(account)} hasArrow>
                          <Text noOfLines={2}>{formatAddress(account)}</Text>
                        </Tooltip>
                      </Td>
                      <Td>
                        <Badge colorScheme={account.has_active_entitlement ? "green" : "gray"}>
                          {account.has_active_entitlement ? "Active" : "Expired"}
                        </Badge>
                      </Td>
                      <Td>
                        <HStack spacing={1}>
                          <Badge colorScheme="blue" fontSize="2xs">
                            {account.purchases.length} total
                          </Badge>
                          {completedPurchases > 0 && (
                            <Badge colorScheme="green" fontSize="2xs">
                              {completedPurchases} paid
                            </Badge>
                          )}
                        </HStack>
                      </Td>
                      <Td onClick={e => e.stopPropagation()}>
                        <IconButton aria-label="Reset password" size="xs"
                          icon={<FaKey />} colorScheme="orange" variant="outline"
                          onClick={() => openPasswordReset(account)}
                        />
                      </Td>
                    </Tr>

                    {/* Expanded purchases grouped view */}
                    {isExpanded && (
                      <Tr>
                        <Td colSpan={8} p={0}>
                          <Box bg={expandedBg} px={6} py={4}>
                            {account.purchases.length === 0 ? (
                              <Text fontSize="sm" color={mutedText}>No purchases.</Text>
                            ) : (
                              <VStack align="stretch" spacing={3}>
                                {account.purchases.map(purchase => {
                                  const isPurchaseExpanded = expandedPurchaseIds.has(purchase.purchase_id);
                                  const isRefunded = purchase.status === "REFUNDED";
                                  const canRefund = purchase.status === "COMPLETED";

                                  return (
                                    <Box key={purchase.purchase_id} bg={purchaseBg}
                                      borderRadius="lg" borderWidth="1px"
                                      borderColor={isRefunded ? "orange.200" : borderColor}
                                      overflow="hidden">

                                      {/* Purchase header */}
                                      <Flex align="center" px={4} py={3}
                                        bg={purchaseHeaderBg} cursor="pointer"
                                        onClick={() => togglePurchase(purchase.purchase_id)}>
                                        <HStack spacing={3} flex={1}>
                                          <Icon as={FaShoppingBag} color="blue.500" />
                                          <Text fontWeight="semibold" fontSize="sm">
                                            {purchase.purchase_reference}
                                          </Text>
                                          <Badge colorScheme={statusColorScheme(purchase.status)}
                                            fontSize="2xs">
                                            {purchase.status}
                                          </Badge>
                                          {purchase.is_test_purchase && (
                                            <Badge colorScheme="gray" fontSize="2xs">TEST</Badge>
                                          )}
                                        </HStack>

                                        <HStack spacing={4} mr={4}>
                                          <Text fontSize="sm" fontWeight="bold">
                                            {purchase.currency} {purchase.total_amount.toFixed(2)}
                                          </Text>
                                          {purchase.completed_at && (
                                            <Text fontSize="xs" color={mutedText}>
                                              {fmtLocal(purchase.completed_at, {
                                                day: "2-digit", month: "short", year: "numeric"
                                              })}
                                            </Text>
                                          )}
                                          <Badge colorScheme="blue" fontSize="2xs">
                                            {purchase.entitlements.length} entitlement{purchase.entitlements.length !== 1 ? "s" : ""}
                                          </Badge>
                                        </HStack>

                                        <HStack spacing={2} onClick={e => e.stopPropagation()}>
                                          <Button size="xs" leftIcon={<FaUndo />}
                                            colorScheme="red" variant="outline"
                                            isDisabled={!canRefund}
                                            onClick={() => openRefund(purchase)}>
                                            {isRefunded ? "Refunded" : "Refund"}
                                          </Button>
                                          <IconButton aria-label="Toggle entitlements"
                                            size="xs" variant="ghost"
                                            icon={isPurchaseExpanded ? <FaChevronDown /> : <FaChevronRight />}
                                            onClick={() => togglePurchase(purchase.purchase_id)}
                                          />
                                        </HStack>
                                      </Flex>

                                      {/* Entitlements under this purchase */}
                                      <Collapse in={isPurchaseExpanded} animateOpacity>
                                        <Box px={4} py={3}>
                                          <Table size="sm" variant="simple">
                                            <Thead>
                                              <Tr>
                                                <Th>Product</Th>
                                                <Th>Type</Th>
                                                <Th>Status</Th>
                                                <Th>Start Date</Th>
                                                <Th>End Date</Th>
                                                <Th>Actions</Th>
                                              </Tr>
                                            </Thead>
                                            <Tbody>
                                              {purchase.entitlements.map(ent => (
                                                <Tr key={ent.entitlement_id}>
                                                  <Td fontSize="sm" fontWeight="medium">
                                                    {ent.practice_set_title ?? `Content ${ent.content_id}`}
                                                  </Td>
                                                  <Td>
                                                    <Badge colorScheme="purple" fontSize="2xs">
                                                      {ent.current_content_type}
                                                    </Badge>
                                                  </Td>
                                                  <Td>
                                                    <Badge
                                                      colorScheme={
                                                        ent.status === "ACTIVE" ? "green" :
                                                        ent.status === "EXPIRED" ? "red" : "gray"
                                                      }
                                                      fontSize="2xs">
                                                      {ent.status}
                                                    </Badge>
                                                  </Td>
                                                  <Td fontSize="xs" color={mutedText}>
                                                    {ent.start_date ? fmtLocal(ent.start_date, {
                                                      day: "2-digit", month: "short", year: "numeric"
                                                    }) : "—"}
                                                  </Td>
                                                  <Td fontSize="xs"
                                                    color={ent.end_date && new Date(ent.end_date) < new Date()
                                                      ? "red.400" : mutedText}>
                                                    {ent.end_date ? fmtLocal(ent.end_date, {
                                                      day: "2-digit", month: "short", year: "numeric"
                                                    }) : "No expiry"}
                                                  </Td>
                                                  <Td>
                                                    <Button
                                                      size="xs"
                                                      colorScheme="blue"
                                                      variant="outline"
                                                      onClick={() =>
                                                        navigate(
                                                          `/practice/app/b2c-account-manager/entitlement/${ent.entitlement_id}/attempts`
                                                        )
                                                      }
                                                    >
                                                      Attempts Progress
                                                    </Button>
                                                  </Td>
                                                </Tr>
                                              ))}
                                            </Tbody>
                                          </Table>
                                        </Box>
                                      </Collapse>
                                    </Box>
                                  );
                                })}
                              </VStack>
                            )}
                          </Box>
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>

          {accounts.length === 0 && !loading && (
            <Box textAlign="center" py={12} color={mutedText}>
              <Text>No accounts found.</Text>
            </Box>
          )}
        </Box>
      )}

      {/* Password reset modal */}
      <Modal isOpen={isPwOpen} onClose={onPwClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Reset Password</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box p={3} bg="orange.50" borderRadius="md" borderWidth="1px"
                borderColor="orange.200">
                <Text fontSize="sm" color="orange.700">
                  Resetting password for <strong>{selectedAccount?.email}</strong>.
                  The student will be prompted to change their password on next login.
                </Text>
              </Box>
              <FormControl>
                <FormLabel fontSize="sm">Temporary Password</FormLabel>
                <Input type="text" value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min. 8 characters" size="sm" />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onPwClose}>Cancel</Button>
            <Button colorScheme="orange" isLoading={pwLoading} onClick={handlePasswordReset}>
              Reset Password
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Refund modal */}
      <Modal isOpen={isRefundOpen} onClose={onRefundClose} isCentered size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Process Refund</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box p={3} bg="red.50" borderRadius="md" borderWidth="1px"
                borderColor="red.200">
                <Text fontSize="sm" color="red.700">
                  Refunding <strong>{selectedPurchase?.purchase_reference}</strong> —{" "}
                  <strong>{selectedPurchase?.currency} {selectedPurchase?.total_amount.toFixed(2)}</strong>.
                  This action cannot be undone.
                </Text>
              </Box>

              <FormControl>
                <FormLabel fontSize="sm">Refund Reason</FormLabel>
                <Select size="sm" value={refundReason}
                  onChange={e => setRefundReason(e.target.value)}
                  placeholder="Select a reason">
                  <option value="requested_by_customer">Requested by customer</option>
                  <option value="duplicate">Duplicate purchase</option>
                  <option value="fraudulent">Fraudulent transaction</option>
                  <option value="product_issue">Product issue</option>
                  <option value="other">Other</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel fontSize="sm">Notes (optional)</FormLabel>
                <Textarea size="sm" value={refundNotes}
                  onChange={e => setRefundNotes(e.target.value)}
                  placeholder="Internal notes about this refund..."
                  rows={3} />
              </FormControl>

              <Divider />

              <FormControl>
                <Checkbox isChecked={expireEntitlements}
                  onChange={e => setExpireEntitlements(e.target.checked)}
                  colorScheme="red">
                  <Text fontSize="sm">
                    Expire all associated entitlements immediately
                  </Text>
                </Checkbox>
                <Text fontSize="xs" color={mutedText} mt={1} ml={6}>
                  Uncheck only if the student should retain access for legal or
                  promotional reasons despite the refund.
                </Text>
              </FormControl>

              {selectedPurchase?.is_test_purchase && (
                <Box p={2} bg="gray.50" borderRadius="md">
                  <Text fontSize="xs" color="gray.500">
                    This is a test purchase — refund will be processed in Stripe sandbox.
                  </Text>
                </Box>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onRefundClose}>Cancel</Button>
            <Button colorScheme="red" isLoading={refundLoading} onClick={handleRefund}
              leftIcon={<FaUndo />}>
              Process Refund
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default B2CAccountManagement;